import React from 'react';
import PropTypes from 'prop-types';
import { Box, List, Grid, ListItem, Typography, ListItemText } from '@material-ui/core';
import clsx from 'clsx';
import i18n from 'i18n-js';
import moment from 'moment';
import { buildUrl, formatAmount } from 'utils';
import { useSelector } from 'react-redux';
import Paginator from 'components/Paginator';
import { useHistory } from 'react-router-dom';
import { useStyles, listTextStyles } from './PeriodsListWidget.styled';
import { capitalize } from 'utils';

const PeriodsListWidget = ({ data, params, pagination, handleChangePage }) => {
  const classes = useStyles();
  const history = useHistory();
  const listClasses = listTextStyles();
  const { localeFormat } = useSelector((state) => state.organizations);

  /**
   * Parse period data
   * @param {object} period Object with period data
   */
  const getPeriodsData = (period = {}) => [
    {
      label: i18n.t('common.invoice'),
      classes: classes.link,
      value: '$' + formatAmount(period?.invoice?.invoiced || 0),
      onClick: () => history.push(buildUrl(`/invoices/${period?.invoice.id}`)),
    },
    {
      label: i18n.t('common.invoice_status'),
      value: period?.invoice?.status
        ? i18n.t(`invoice_statuses.${period?.invoice?.status}`, {
            defaultValue: capitalize(period?.invoice?.status?.toLowerCase() || '--'),
          })
        : '--',
    },
    {
      label: i18n.t('common.vehicle'),
      value: period?.vehicle?.display_name || '--',
    },
    {
      label: i18n.t('common.location'),
      value: period?.vehicle?.location?.name || '--',
    },
  ];

  const TimezoneComponent = ({ tz = moment.tz.guess() }) => (
    <span className={classes.timezone}>{tz ? moment().tz(tz).format('(UTC Z)') : ''}</span>
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      className={classes.listBox}
      justifyContent="space-between"
    >
      {data?.length ? (
        <>
          <List className={classes.listRoot} data-cy="periods-list">
            {data?.map((period, index) => {
              const tz = period?.vehicle?.vehicle_group?.time_zone || moment.tz.guess();
              return (
                <Box
                  key={index}
                  data-cy="periods-item"
                  className={clsx(classes.container, classes?.[period.status])}
                >
                  <Box className={classes.header} width="100%">
                    <Typography
                      style={{ lineHeight: 1 }}
                      className={clsx(classes.status, classes?.[period.status])}
                    >
                      <Box
                        fontSize={11}
                        fontWeight="fontWeightMedium"
                        component="span"
                        lineHeight={1}
                      >
                        {i18n.t(`reservations_statuses.${period.status}`)}
                      </Box>
                    </Typography>
                  </Box>
                  <Box mt={2} width="100%">
                    <Grid container>
                      <Grid item xs={6}>
                        <Typography className={clsx(classes.textSubtitle, classes.lineHeight)}>
                          <Box
                            mt={2}
                            fontSize={13}
                            component="span"
                            fontWeight="fontWeightBold"
                            data-cy="period-start-reserved"
                          >
                            {i18n.t('reservations.start_reserved')}
                          </Box>
                        </Typography>
                        <Typography className={clsx(classes.textSubtitle, classes.lineHeight)}>
                          <Box mt={2} fontSize={13} lineHeight={1} component="span">
                            {period?.start_used
                              ? moment(period.start_used).tz(tz).format(`${localeFormat} h:mm A`)
                              : '-'}
                            {period?.start_used && <TimezoneComponent tz={tz} />}
                          </Box>
                        </Typography>
                      </Grid>
                      <Grid item sm={12} md={6}>
                        <Typography className={clsx(classes.textSubtitle, classes.lineHeight)}>
                          <Box
                            mt={2}
                            fontSize={13}
                            component="span"
                            fontWeight="fontWeightBold"
                            data-cy="period-end-reserved"
                          >
                            {i18n.t('reservations.end_reserved')}
                          </Box>
                        </Typography>
                        <Typography className={clsx(classes.textSubtitle, classes.lineHeight)}>
                          <Box mt={2} fontSize={13} lineHeight={1} component="span">
                            {period?.end_used
                              ? moment(period.end_used).tz(tz).format(`${localeFormat} h:mm A`)
                              : '-'}
                            {period?.end_used && <TimezoneComponent tz={tz} />}
                          </Box>
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                  <List className={classes.list}>
                    {getPeriodsData(period).map((val, index) => (
                      <ListItem key={index} className={classes.listItem}>
                        <ListItemText
                          classes={listClasses}
                          primary={val.label}
                          secondary={
                            <Box
                              display="flex"
                              component="span"
                              flexDirection="column"
                              className={classes.textRight}
                              data-cy={`period-${val.label}`}
                            >
                              <Typography
                                variant="body2"
                                component="span"
                                className={val?.classes}
                                onClick={() => {
                                  if (val.onClick) val.onClick();
                                }}
                              >
                                {val.value}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              );
            })}
          </List>
          <Paginator
            component="div"
            limit={params.limit}
            pagination={pagination}
            onPageChange={handleChangePage}
          />
        </>
      ) : (
        <Typography color="textPrimary" style={{ lineHeight: 1 }}>
          <Box fontSize={14} component="span" lineHeight={1}>
            {i18n.t('common.empty_list')}
          </Box>
        </Typography>
      )}
    </Box>
  );
};

PeriodsListWidget.propTypes = {
  params: PropTypes.object,
  pagination: PropTypes.object,
  reservations: PropTypes.array,
  handleChangePage: PropTypes.func,
};

PeriodsListWidget.defaultProps = {
  params: {
    page: 1,
    limit: 3,
  },
  pagination: {},
  reservations: [],
};

export default PeriodsListWidget;
